/**
 * Modular translation dictionaries.
 *
 * WHY THIS EXISTS: all translations used to live in one 1,400-line object inside
 * LanguageContext.tsx. Adding a screen's worth of strings meant editing that one
 * file, which made parallel work impossible and merges painful. Each area now
 * owns its own dictionary file; LanguageContext merges them over its base keys.
 *
 * CONTRACT for a dictionary file:
 *   export const <area>Dict: AreaDict = { en: { key: 'English' }, de: { key: 'Deutsch' } };
 *   — every key present in `en` MUST also exist in `de` (checked by
 *     scripts/check-i18n-parity.js, run in CI/pre-deploy).
 *   — keys are prefixed per area (tp = tenant portal, ceo, gp = guest portal,
 *     pm = property management, doc = documents, pr = portfolio report, ip =
 *     investor portal) so two areas can never collide.
 *
 * Base keys in LanguageContext win on conflict, so an area dictionary can never
 * silently change a shared label.
 */

import { portalsDict } from './portals';
import { documentsDict } from './documents';
import { investorDict } from './investor';
import { propertyDict } from './property';
import { adminDict } from './admin';
import { calculatorDict } from './calculator';

/** Shape every area dictionary must follow: matching `en` and `de` key sets. */
export interface AreaDict {
  en: Record<string, string>;
  de: Record<string, string>;
}

const AREA_DICTS: AreaDict[] = [
  portalsDict,
  documentsDict,
  investorDict,
  propertyDict,
  adminDict,
  calculatorDict,
];

/** Merged area translations, by language. Base LanguageContext keys override these. */
export const areaTranslations = {
  en: Object.assign({}, ...AREA_DICTS.map((d) => d.en)) as Record<string, string>,
  de: Object.assign({}, ...AREA_DICTS.map((d) => d.de)) as Record<string, string>,
};
